<div class="connect-container">
    <!-- Left column: main content -->
    <div class="connect-main">
        <!-- Browser Support Check -->
        <div
            *ngIf="!isSupported"
            class="support-status"
            [ngClass]="{ supported: isSupported, 'not-supported': !isSupported }"
        >
            <div class="status-icon">
                {{ isSupported ? '✓' : '✗' }}
            </div>
            <div class="status-text">
                <strong>Web Serial API:</strong>
                {{ isSupported ? 'Supported' : 'Not Supported' }}
            </div>
            <div class="status-description" *ngIf="!isSupported">
                This browser doesn't support the Web Serial API. Please use Chrome or Edge.
            </div>
        </div>

        <!-- Connection Controls -->
        <div class="connection-controls">
            <div class="connection-status">
                <div class="status-indicator" [ngClass]="{ connected: isConnected, disconnected: !isConnected }">
                    {{ isConnected ? '🟢' : '🔴' }}
                </div>
                <span class="status-label">
                    {{ isConnected ? 'Connected' : 'Disconnected' }}
                </span>
            </div>

            <div class="control-buttons">
                <div class="select-location" *ngIf="(locations$ | async)?.length > 0">
                    <app-dropdown
                        [items]="locations$ | async"
                        [(ngModel)]="selectedLocationId"
                        [searchable]="true"
                        placeholder="{{ 'FEATURES.MEASUREMENTS.SELECT_LOCATION' | translate }}"
                    />
                </div>
                <button
                    class="connect-btn"
                    [disabled]="!isSupported || isConnecting || isConnected"
                    (click)="onConnect()"
                >
                    {{ isConnecting ? 'Connecting...' : 'Connect' }}
                </button>
            </div>

            <!-- Skip Upload Checkbox -->
            <div class="connection-controls-subsection">
                <app-checkbox
                    [(ngModel)]="skipUpload"
                    (change)="onSkipUploadChange()"
                    [disabled]="isTransferring || isUploadInProgress"
                    [text]="'Store locally only' | translate"
                    description="Skip upload to server when experiencing poor connection"
                />
                <button
                    *appHasPermissions="[permissions.InternalUser]"
                    (click)="showManuallyImport = !showManuallyImport"
                    class="text"
                    type="button"
                >
                    <span class="material-symbols-rounded leading" aria-hidden="true" *ngIf="showManuallyImport">
                        visibility
                    </span>
                    <span class="material-symbols-rounded leading" aria-hidden="true" *ngIf="!showManuallyImport">
                        visibility_off
                    </span>
                    <span>Import Manually</span>
                </button>
                <button (click)="showLog = !showLog" class="text" type="button">
                    <span class="material-symbols-rounded leading" aria-hidden="true" *ngIf="showLog">
                        visibility
                    </span>
                    <span class="material-symbols-rounded leading" aria-hidden="true" *ngIf="!showLog">
                        visibility_off
                    </span>
                    <span>Log</span>
                </button>
            </div>
        </div>

        <!-- Error Display -->
        <div class="error-message" *ngIf="connectionError">
            <div class="error-icon">⚠️</div>
            <div class="error-text">{{ connectionError }}</div>
        </div>

        <!-- File Import Section -->
        <div *ngIf="showManuallyImport" class="file-import-section">
            <div class="import-header">
                <h3>Import Files Manually</h3>
                <p class="subtitle">Upload measurement files directly from your computer</p>
            </div>

            <div class="import-controls">
                <input
                    type="file"
                    #fileInput
                    (change)="onFileSelect($event)"
                    accept=".json,.txt"
                    multiple
                    style="display: none"
                />
                <button class="import-btn" (click)="fileInput.click()">
                    <span class="material-symbols-rounded" aria-hidden="true">upload_file</span>
                    <span>Select Files</span>
                </button>
            </div>

            <div
                class="drop-zone"
                [class.drag-over]="isDragOver"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)"
            >
                <div class="drop-zone-content">
                    <span class="material-symbols-rounded drop-icon" aria-hidden="true">cloud_upload</span>
                    <p class="drop-text">Drag and drop measurement files here</p>
                    <p class="drop-hint">or use the "Select Files" button above</p>
                </div>
            </div>
        </div>

        <!-- File Processing Pipeline -->
        <div class="file-pipeline-panel" *ngIf="isConnected || fileList.length > 0">
            <!-- Unified File List -->
            <div class="unified-file-list" *ngIf="fileList.length > 0 || isTransferring">
                <div class="panel-header">
                    <div>
                        <h4>
                            Files
                            {{ fileList.length > 0 ? '(' + (fileList.length + (isTransferring ? 1 : 0)) + ')' : '' }}
                        </h4>
                    </div>
                    <div class="pipeline-controls">
                        <button class="refresh-btn" (click)="onRefreshUploadStatus()">
                            <span class="material-symbols-rounded leading" aria-hidden="true">refresh</span>
                            <span>Refresh</span>
                        </button>
                        <button
                            class="retry-btn"
                            [disabled]="uploadStatus.pending === 0 && uploadStatus.failed === 0"
                            (click)="onRetryUpload()"
                        >
                            <span class="material-symbols-rounded leading" aria-hidden="true">refresh</span>
                            <span>Retry upload ({{ uploadStatus.failed + uploadStatus.pending }})</span>
                        </button>
                        <button (click)="onClearStoredFiles()">
                            <span>Clear All</span>
                            <span class="material-symbols-rounded leading" aria-hidden="true">clear</span>
                        </button>
                    </div>
                </div>

                <div class="file-list">
                    <!-- Currently transferring file -->
                    <div class="file-item transferring-item" *ngIf="isTransferring && currentFileName">
                        <div class="file-info">
                            <div class="file-name">{{ currentFileName }}</div>
                            <div class="file-details">
                                Receiving via YMODEM... | File #{{ filesReceived + 1 }}
                                <span *ngIf="transferSpeedBps > 0">
                                    | {{ (transferSpeedBps / 1024).toFixed(1) }} KB/s</span
                                >
                            </div>
                            <div class="file-progress" *ngIf="transferProgress > 0">
                                <div class="progress-bar small">
                                    <div class="progress-fill transferring" [style.width.%]="transferProgress"></div>
                                </div>
                                <span class="progress-text">{{ transferProgress.toFixed(1) }}%</span>
                            </div>
                        </div>
                        <div class="file-status transferring">
                            <span class="material-symbols-rounded" aria-hidden="true">download</span>
                            <span>Transferring</span>
                        </div>
                    </div>

                    <!-- Processed files -->
                    <div
                        class="file-item"
                        *ngFor="let file of fileList; trackBy: trackById"
                        [ngClass]="'status-' + file.status"
                    >
                        <div class="file-info">
                            <div class="file-name">{{ file.metadata.filename }}</div>
                            <div class="file-details">
                                Size: {{ file.metadata.fileSize | number }} bytes | Device: {{ file.metadata.device }} |
                                {{ getLocationName(file.locationId) }} | File #{{ file.metadata.fileNumber }}
                                <span *ngIf="file.timestamp"> | {{ file.timestamp | date: 'medium' }}</span>
                            </div>
                            <!-- Upload progress for uploading files -->
                            <div class="file-progress" *ngIf="file.status === 'uploading'">
                                <div class="progress-bar small">
                                    <div class="progress-fill uploading sliding"></div>
                                </div>
                                <span class="progress-text">Uploading...</span>
                            </div>
                        </div>

                        <div class="file-error" *ngIf="file.error && file.status === 'failed'">
                            {{ file.error }}
                        </div>
                        <div class="file-status" [ngClass]="file.status">
                            <span class="material-symbols-rounded" aria-hidden="true">
                                {{ getStatusIcon(file.status) }}
                            </span>
                            <span>{{ getStatusText(file.status) }}</span>
                            <span *ngIf="file.retryCount > 0" class="retry-count">(retry {{ file.retryCount }})</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Empty state -->
            <div class="empty-state" *ngIf="fileList.length === 0 && !isTransferring">
                <div class="empty-icon">📁</div>
                <div class="empty-text">No files in pipeline</div>
                <div class="empty-description">Files will appear here as they are transferred and uploaded</div>
            </div>
        </div>

        <!-- Log -->
        <div *ngIf="showLog" class="received-data">
            <div class="file-pipeline-panel">
                <div class="panel-header">
                    <h3>Received Data ({{ receivedData.length }} messages)</h3>
                    <div class="pipeline-controls">
                        <button *ngIf="receivedData.length > 0" (click)="onClearData()">
                            <span>Clear log</span>
                            <span class="material-symbols-rounded leading" aria-hidden="true">clear</span>
                        </button>
                    </div>
                </div>

                <div class="data-container">
                    <div class="data-item" *ngFor="let data of receivedData; trackBy: trackByIndex">
                        {{ data }}
                    </div>
                    <div class="no-data" *ngIf="receivedData.length === 0">No data received yet...</div>
                </div>
            </div>
        </div>
    </div>
    <!-- /.connect-main -->

    <!-- Right column: always-visible instructions -->
    <div class="connect-sidebar">
        <div class="instructions">
            <h3>Transferring Data from TetraGraph<sup>®</sup></h3>
            <ol>
                <li>Power on the TetraGraph device.</li>
                <li>Connect the device to your computer using a USB-C cable.</li>
                <li>
                    In <strong>TetraAnalytics</strong>
                    <ol>
                        <li>
                            Select the target location from the dropdown menu. This option appears only if one or more
                            locations are configured in your workspace.
                        </li>
                        <li>Click the <span class="inline-btn">Connect</span> button to initiate connection.</li>
                        <li>
                            Select the <strong>STM32 USB Device</strong> in the dialog and click connect.
                            <img
                                class="connect-device-img"
                                src="assets/images/connect_device_popup.png"
                                alt="STM32 USB Device"
                            />
                        </li>
                        <li>Confirm connection via the status indicator in TetraAnalytics.</li>
                    </ol>
                </li>
                <li>
                    On <strong>TetraGraph</strong>
                    <ol>
                        <li>
                            Navigate to <strong>Settings</strong>
                            <img src="assets/help/gear.png" class="inline-img" alt="Settings" /> →
                            <strong>Data</strong> <img src="assets/help/file.png" class="inline-img" alt="Data" /> →
                            <strong>Upload</strong>
                            <img src="assets/help/upload.png" class="inline-img" alt="Upload" />
                        </li>
                        <li>
                            Select the desired cases and log files, then tap
                            <span class="inline-btn send">Send selected files</span>
                        </li>
                    </ol>
                </li>
            </ol>

            <div class="instructions-note caution">
                <strong>Caution ⚠️</strong>
                <p>
                    Do not leave the page during transfer, as this will interrupt the upload. Duplicate files are not
                    stored.
                </p>
            </div>

            <div class="instructions-note help">
                <strong>Help ℹ️</strong>
                <p>
                    Refer to the Instructions for Use (<a routerLink="/help">IFU</a>) or contact our support team at
                    <a href="mailto:analytics@senzime.com">analytics&#64;senzime.com</a>
                </p>
            </div>
        </div>
    </div>
    <!-- /.connect-sidebar -->
</div>
